/* ============================================================
   PDP REDESIGN — sticky ATC visibility, price sync, size sync.
   Additive. Scoped to the redesigned product template only.
   Relies on the theme's global pub/sub (subscribe / PUB_SUB_EVENTS)
   and product-form (submit handled by form= attribute).
   All lookups are guarded so nothing throws if a piece is absent.
   ============================================================

   EXTERNAL WIDGET DEPENDENCIES — READ BEFORE DEBUGGING FLOATING UI
   ----------------------------------------------------------------
   The sticky Add-to-Cart bar occupies the bottom-right corner of the viewport,
   where three third-party widgets also live. Keeping them off the bar means
   reaching into markup we do not own, so each hook below is a contract with a
   vendor that can change without notice. NONE of them throw when they break —
   the symptom is always visual, which is why they are listed here.

   1. Redo chat widget  (repositioned by this file)
      - `#redo-chat-widget` in the light DOM is a SHADOW HOST, and it is
        injected asynchronously, so it may not exist at DOMContentLoaded.
      - inside its shadowRoot: `#redo-chat-widget-container`, a z-index wrapper.
      - inside THAT: the element actually carrying `position: fixed` /
        `bottom: 20px`. Its class names are build-hashed, so we cannot select
        it — findFixedChatEl() walks the divs and matches on computed
        `position === 'fixed'`, then caches the hit.
      - what we do: set `bottom` 20px -> 65px `!important` while the sticky bar
        is shown (CHAT_DEFAULT_BOTTOM / CHAT_LIFTED_BOTTOM below).
      - if Redo changes: the bubble sits behind or on top of the sticky bar.
        Re-check by opening the shadow root in devtools and confirming (a) both
        ids, and (b) that exactly one descendant computes to position: fixed.

   2. Redo Concierge "Ask AI" prompt  (hidden in pdp-redesign.css)
      - `#redo-concierge-product-form-target` is the only stable handle; the
        widget's internals sit in an open shadow root with hashed classes.
      - `!important` is required because Redo's own JS writes
        `style="display: unset"` inline on the host.
      - hiding it does NOT stop the app loading or its analytics. Disable the
        Redo app embed to remove it for real.
      - if Redo changes: the prompt reappears on the redesigned PDP.

   3. Zoho SalesIQ bubble  (lifted in pdp-redesign.css)
      - `.zsiq_floatmain` sits in the light DOM with a plain vendor class, so
        plain CSS reaches it — no JS needed. It rides the same
        `body.pdp-sticky-shown` + `--pdp-sticky-height` mechanism as the
        theme's own `.back-to-top` FAB.
      - if Zoho renames that class: the bubble overlaps the sticky bar.

   The bar's live height is published as `--pdp-sticky-height` on <html> by
   syncStickyHeight() below; anything else that needs to clear the bar should
   consume that variable rather than hardcoding a height.
   ============================================================ */
(function () {
  function init() {
    var main = document.querySelector('product-info.pdp-main');
    var sticky = document.querySelector('[data-pdp-sticky]');
    if (!main || !sticky) return;

    // Mark the redesign PDP so page-scoped rules (e.g. the flush back-to-top)
    // don't leak onto other templates that share the global back-to-top FAB.
    document.body.classList.add('pdp-redesign-page');

    /* --- Show/hide the sticky bar based on the buy-box cart area --- */
    var anchor = main.querySelector('.pdp-buybox__cart') || main.querySelector('.pdp-buybox');

    // Publish the sticky bar's live height so the back-to-top FAB can sit
    // exactly on top of it (see .pdp-sticky rules in pdp-redesign.css).
    function syncStickyHeight() {
      document.documentElement.style.setProperty(
        '--pdp-sticky-height', sticky.offsetHeight + 'px'
      );
    }

    function setStickyVisible(show) {
      if (show) syncStickyHeight();
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      // Body class (not :has) drives the back-to-top lift so it reliably
      // drops back to the bottom of the screen when the bar hides.
      document.body.classList.toggle('pdp-sticky-shown', show);
      // The Redo chat widget renders in a shadow root, so the CSS that lifts
      // .back-to-top / .zsiq_floatmain can't reach it — nudge it from JS.
      positionChatWidget(show);
    }

    /* --- Lift the Redo chat bubble above the sticky ATC bar ---
       #redo-chat-widget is a shadow host. Inside it, #redo-chat-widget-container
       is only a z-index wrapper; the element that actually carries
       position:fixed / bottom:20px is a hashed-class child of it. Classes are
       build-hashed, so find the fixed child by computed position rather than
       by an unstable selector. */
    var CHAT_DEFAULT_BOTTOM = '20px';
    var CHAT_LIFTED_BOTTOM = '65px';
    // How long to keep waiting for the widget to load before giving up, so a
    // page where Redo never appears doesn't observe the body forever.
    var CHAT_WAIT_MS = 30000;
    var chatShadowObserved = false;
    var chatFixedEl = null;

    // Coalesce a burst of mutations into a single run on the next frame. The
    // cart drawer and the review widgets each fire hundreds of mutations at
    // once, and every one of them used to trigger a full re-scan.
    function onNextFrame(fn) {
      var scheduled = false;
      return function () {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function () {
          scheduled = false;
          fn();
        });
      };
    }

    function findFixedChatEl(container) {
      // Cached hit stays valid until the widget re-renders and drops the node.
      if (chatFixedEl && chatFixedEl.isConnected) return chatFixedEl;
      var candidates = container.querySelectorAll('div');
      for (var i = 0; i < candidates.length; i++) {
        if (getComputedStyle(candidates[i]).position === 'fixed') {
          chatFixedEl = candidates[i];
          return chatFixedEl;
        }
      }
      return null;
    }
    function positionChatWidget(lift) {
      var host = document.getElementById('redo-chat-widget');
      var root = host && host.shadowRoot;
      var container = root && root.getElementById('redo-chat-widget-container');
      if (!container) return false;
      // Watch the shadow tree once it exists — a body observer can't see
      // re-renders inside the shadow root, so this catches the widget
      // rebuilding itself after we've set a state. Scoped to the widget and
      // debounced, so a chatty re-render costs one pass per frame at most.
      if (!chatShadowObserved && 'MutationObserver' in window) {
        chatShadowObserved = true;
        new MutationObserver(onNextFrame(reapplyChatWidget))
          .observe(root, { childList: true, subtree: true });
      }
      var fixed = findFixedChatEl(container);
      if (!fixed) return false;
      fixed.style.setProperty(
        'bottom', lift ? CHAT_LIFTED_BOTTOM : CHAT_DEFAULT_BOTTOM, 'important'
      );
      return true;
    }
    function reapplyChatWidget() {
      return positionChatWidget(document.body.classList.contains('pdp-sticky-shown'));
    }

    /* The widget loads asynchronously, so watch for the shadow host appearing.
       This observer exists ONLY to catch that first appearance: it disconnects
       as soon as the widget is found (the shadow observer above takes over from
       there) and after CHAT_WAIT_MS regardless. Without that it sat on the whole
       body for the life of the page, re-scanning on every unrelated mutation
       from the cart drawer, review widgets and swatch swaps. */
    if ('MutationObserver' in window && !reapplyChatWidget()) {
      var bodyObserver = new MutationObserver(onNextFrame(function () {
        if (reapplyChatWidget()) bodyObserver.disconnect();
      }));
      bodyObserver.observe(document.body, { childList: true, subtree: true });
      window.setTimeout(function () {
        bodyObserver.disconnect();
      }, CHAT_WAIT_MS);
    }

    if (anchor && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          setStickyVisible(!entry.isIntersecting);
        });
      }, { threshold: 0 });
      observer.observe(anchor);
      window.addEventListener('resize', syncStickyHeight);
    }

    /* --- Keep sticky + button price in sync with the main price --- */
    function syncPrices() {
      var source = main.querySelector('.pdp-price__current');
      if (!source) return;
      var text = source.textContent.trim();
      document.querySelectorAll('[data-pdp-atc-price]').forEach(function (el) {
        el.textContent = text;
      });
      // Reflect availability on the sticky button
      var mainSubmit = main.querySelector('.product-form__submit');
      var stickySubmit = sticky.querySelector('[data-pdp-sticky-submit]');
      if (mainSubmit && stickySubmit) {
        var disabled = mainSubmit.hasAttribute('disabled');
        stickySubmit.toggleAttribute('disabled', disabled);
      }
    }

    /* --- Sticky <select> follows the main variant selection ---
       The theme replaces the sticky bar's innerHTML on every variant change
       (updateSourceFromDestination("ProductStickyCart")), so re-query the
       select each time rather than caching it. */
    function syncStickySizeFromMain() {
      var stickySize = sticky.querySelector('[data-pdp-sticky-size]');
      var selects = main.querySelector('product-variant-selects');
      if (!stickySize || !selects) return;
      var opts = Array.prototype.map.call(stickySize.options, function (o) { return o.value; });
      var current = null;
      var checked = Array.prototype.find.call(
        selects.querySelectorAll('input[type="radio"]:checked'),
        function (el) { return opts.indexOf(el.value) !== -1; }
      );
      if (checked) current = checked.value;
      if (!current) {
        Array.prototype.forEach.call(selects.querySelectorAll('select'), function (sel) {
          if (opts.indexOf(sel.value) !== -1) current = sel.value;
        });
      }
      if (current && stickySize.value !== current) stickySize.value = current;
    }

    if (window.subscribe && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        // Let the DOM settle after the theme swaps #price-* and the sticky bar.
        window.requestAnimationFrame(function () {
          syncPrices();
          syncStickySizeFromMain();
        });
      });
    }

    /* --- Sticky size change drives the main variant selection ---
       Delegated on document so it keeps working after the theme re-renders
       (and thus replaces) the sticky bar's inner elements. */
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('[data-pdp-sticky-size]')) return;
      if (!sticky.contains(target)) return;

      var value = target.value;
      var selects = main.querySelector('product-variant-selects');
      if (!selects) return;

      // Button/swatch pickers render radios…
      var input = Array.prototype.find.call(
        selects.querySelectorAll('input[type="radio"]'),
        function (el) { return el.value === value; }
      );
      if (input) {
        if (!input.checked) input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // …dropdown pickers render a <select>.
      var dropdown = Array.prototype.find.call(
        selects.querySelectorAll('select'),
        function (sel) {
          return Array.prototype.some.call(sel.options, function (o) { return o.value === value; });
        }
      );
      if (dropdown) {
        dropdown.value = value;
        dropdown.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Initial alignment in case the sticky default differs from the main.
    syncStickySizeFromMain();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
